<script setup lang="ts">
/**
 * Renders the form itself, without the PDF preview. This is just the form fields and signature pads and it returns
 * its result via v-model.
 */
import LegalFormSignature from "@/components/LegalFormSignature.vue"
import { ZipValidationMessage } from "@/utils"
import { useAppConfig, useHttpClient } from "@/vf"
import { DateTime } from "luxon"
import { computed, inject, reactive, ref, unref, useTemplateRef, watch, watchEffect } from "vue"
import type { ConditionFormData } from "."

const props = defineProps<{
    formIds?: string[]
    condition?: string
    formId?: string // html id attribute of the <form> element
    organizations?: { id: string; name: string; label: string }[]
    modelValue?: ConditionFormData
    preProvidedFields?: string[]
    memberAge?: number
    formErrors?: { [key: string]: string[] }

    /**
     * If true, the signature fields will not be shown in the form and validation will not be performed.
     */
    hideSignatureFields?: boolean
}>()

interface FormField {
    id: string
    type: string
    text: string
    key: string
    config: any
    choices: string
    section: string
    index: number
}

const emit = defineEmits<{
    (e: "update:modelValue", value: ConditionFormData): void
    (e: "formChanged", value: any): void
    (e: "submit", value: HTMLFormElement): void
    (e: "hasEmptySignatureFields", value: boolean): void
    (e: "validate", valid: boolean): void
}>()

const formErrors = computed(() => props.formErrors ?? {})

/**
 * Alpha 2 to name map
 */
const countries = ref<Record<string, string>>({})

const form = ref<FormField[]>([])
const http = useHttpClient()
const { appConfig } = useAppConfig()

const formRef = useTemplateRef("formRef")

// make this a 2-way binding
const formData = computed<ConditionFormData>({
    get: () => props.modelValue ?? ({} as ConditionFormData),
    set: value => {
        emit("update:modelValue", value)
    },
})

watch(
    formData.value,
    () => {
        const newStreetAndNumber = ((formData.value.street ?? "") + "\t" + (formData.value.streetNumber ?? "")).trim()
        if (formData.value["street+streetNumber"] != newStreetAndNumber) {
            formData.value["street+streetNumber"] = newStreetAndNumber
        }
        const newZipAndCity = ((formData.value.zip ?? "") + "\t" + (formData.value.city ?? "")).trim()
        if (formData.value["zip+city"] != newZipAndCity) {
            formData.value["zip+city"] = newZipAndCity
        }
        emit("update:modelValue", formData.value)
    },
    { deep: true },
)

watch(
    () => [props.formIds, props.condition],
    async () => {
        if (props.formIds && props.condition) {
            throw Error("You need to specify either formId or condition, not both.")
        }
        if (!props.formIds && !props.condition) {
            throw Error("You need to specify either formId or condition.")
        }
        const params: Record<string, string> = {}
        if (props.formIds) {
            params["ids"] = props.formIds.join(",")
        }
        if (props.condition) {
            params["required"] = props.condition
        }
        const data = (
            await http.get<{
                forms: any[]
                countries: Record<string, string>
                organizations: { name: string; label: string }[]
            }>("legal/form/", params)
        ).data

        form.value = data.forms ?? []
        countries.value = data.countries
        emit("formChanged", form.value)
        validate()
    },
    { immediate: true },
)

const hiddenFields = unref(inject<string[]>("ConditionForm.HiddenFields", [])) ?? []

/**
 * Find the best matching age for the current member. This is used to determine if a field should be visible or not.
 * It prefers the memberAge prop, if it is set. Otherwise, it will use the birthDate field from the formData.
 * If none are provided, it will return null.
 */
const computedAge = computed(() => {
    if (props.memberAge) {
        return props.memberAge
    }

    const birthDate = formData.value.birthDate

    if (birthDate) {
        return Math.floor(DateTime.now().diff(DateTime.fromISO(birthDate), "years").years)
    }

    return null
})

function elementShouldBeVisible(formElement: FormField) {
    // check if the formElement should be visible for the current memberAge
    const age = computedAge.value
    const isVisibleForMemberByAge = !age || formElement.config.visibility !== "u18" || age < 18

    // i don't know why this is only for these types and had no time to investigate
    const isValidType =
        formElement.type === "signature" ||
        formElement.type === "input" ||
        formElement.type === "radio" ||
        formElement.type === "date"

    // check if the field is already provided; this is used to hide specific fields from the form that is already known
    // and must not be prompted again
    const isProvidedField = props.preProvidedFields?.includes(formElement.config.mapping)

    // outer components can hide fields that should never be visible (e.g. organization field for user athletes)
    const isHiddenField = hiddenFields.includes(formElement.config.mapping)

    const isSignatureAndShouldBeHidden = props.hideSignatureFields && formElement.type === "signature"

    // console.log(
    //     "formElement visible: ",
    //     formElement.key,
    //     `by age (${props.memberAge}):`,
    //     isVisibleForMemberByAge,
    //     "is valid type:",
    //     isValidType,
    //     "visible by provided field: ",
    //     !isProvidedField,
    // )

    return isValidType && isVisibleForMemberByAge && !isProvidedField && !isHiddenField && !isSignatureAndShouldBeHidden // && isNotMapped
}

// Computed property to get visible signature fields
const signatureFields = computed(() => form.value.filter(i => i.type === "signature" && elementShouldBeVisible(i)))

// State for signature fields empty status
const signatureFieldsState = reactive<Record<string, { isEmpty: boolean }>>({})

// Watch for changes in visible signature fields to initialize their state and clean up hidden fields
watch(
    () => signatureFields.value,
    visibleFields => {
        // Get set of visible field keys
        const visibleKeys = new Set(visibleFields.map(field => field.key))

        // Remove entries for fields that are no longer visible
        Object.keys(signatureFieldsState).forEach(key => {
            if (!visibleKeys.has(key)) {
                delete signatureFieldsState[key]
            }
        })

        // Initialize state for new visible fields
        visibleFields.forEach(field => {
            if (!(field.key in signatureFieldsState)) {
                signatureFieldsState[field.key] = { isEmpty: true }
            }
        })

        validate()
    },
    { immediate: true },
)

// When the "empty" state of a signature field changes, this gets triggered. It will then emit the "hasEmptySignatureFields"
// event to notify the parent element about the change.
function onEmpty(isEmpty: boolean, signatureElement: FormField) {
    console.log("Signature field empty state changed", signatureElement.key, isEmpty)

    // Update the signature field state
    signatureFieldsState[signatureElement.key] = { isEmpty }

    // this will check the whole form and emit the validate event
    validate()
}

// For initial state, we assume that all signature fields are empty and inform the parent element about it.
// Once we know which fields this form contains, we update the state. If there are no signature fields, this
// will then change the state back to hasEmptySignatureFields = false. This makes handling the state easier
// for the parent element.
emit("hasEmptySignatureFields", true)
emit("validate", false)

function hasEmptySignatureFields() {
    return Object.values(signatureFieldsState).some((i: any) => i.isEmpty)
}

const sections = computed(() => {
    // sort data.forms by field "section"
    const allFields = [...form.value].sort((a, b) => {
        if (a.section != b.section) {
            return a.section.localeCompare(b.section)
        }

        return a.index - b.index
    })

    const sections: Record<string, FormField[]> = {}

    allFields.forEach(field => {
        if (!sections[field.section]) {
            sections[field.section] = []
        }

        sections[field.section].push(field)
    })

    return sections
})

/*─────────────────────────────────────┐
│  validate event                      │
└─────────────────────────────────────*/

// Update the validate event when the form changes (true if all required fields and signature fields are filled out,
// false otherwise)
watchEffect(() => validate())

function validate() {
    emit("hasEmptySignatureFields", hasEmptySignatureFields())

    const valid = formIsValid()
    console.log("validate event triggered valid=", valid)
    emit("validate", valid)
}

function formIsValid() {
    const signatureValid = !hasEmptySignatureFields()
    const inputsValid = !hasEmptyRequiredInputFields()

    console.log("formIsValid", { signatureValid, inputsValid })

    return signatureValid && inputsValid
}

watch(
    formData,
    () => {
        // when the form changes, we need to revalidate it
        validate()
    },
    { deep: true },
)

function hasEmptyRequiredInputFields(): boolean {
    if (!formRef.value) {
        console.log("Form is not loaded yet")
        // when form is not loaded yet, assume it's invalid
        return true
    }

    const formData = new FormData(formRef.value)

    for (const section in sections.value) {
        for (const formElement of sections.value[section]) {
            if (!elementShouldBeVisible(formElement)) {
                // invisible fields are not required
                continue
            }

            if (!formElement.config.required) {
                // not required fields can be ignored
                continue
            }

            const data =
                formData.get(`part[${formElement.id}][0]`)?.toString().trim() ??
                formData.get(`part[${formElement.id}]`)?.toString().trim() ??
                ""

            if (data.length === 0) {
                console.log("Found empty required field", formElement.key)
                return true
            }
        }
    }

    return false
}
</script>

<template>
    <div v-if="form.length > 0">
        <form
            method="post"
            @submit.prevent="ev => emit('submit', ev.target as HTMLFormElement)"
            :id="props.formId"
            ref="formRef"
        >
            <template v-for="(elements, sectionName) of sections" :key="sectionName">
                <div class="bg-primary-dark text-white py-2 px-4 font-weight-bold">{{ sectionName }}</div>
                <div class="pl-sm-4 mb-5">
                    <div v-for="formElement of elements" :key="formElement.key">
                        <template v-if="elementShouldBeVisible(formElement)">
                            <div class="my-3" v-if="formElement.type === 'input'">
                                <label class="d-block" :class="{ 'font-weight-bold': formElement.config.required }">
                                    {{ formElement.text }}
                                </label>
                                <div class="row" v-if="formElement.config.mapping === 'street+streetNumber'">
                                    <input
                                        type="hidden"
                                        :name="'part[' + formElement.id + '][0]'"
                                        :value="`${formData['street'] ?? ''}\t${formData['streetNumber'] ?? ''}`"
                                    />
                                    <div class="col-md-6">
                                        <div class="form-group flex-column" data-type="text">
                                            <div class="flex-grow-1 d-flex">
                                                <label for="street" class="col-form-label required">
                                                    {{ $t("@cms:onlineForm.street") }}
                                                </label>
                                                <div class="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="street"
                                                        v-model="formData['street']"
                                                        :required="!!formElement.config.required"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group flex-column" data-type="text">
                                            <div class="flex-grow-1 d-flex">
                                                <label for="streetNumber" class="col-form-label required">
                                                    {{ $t("@cms:onlineForm.streetNumber") }}
                                                </label>
                                                <div class="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="streetNumber"
                                                        maxlength="10"
                                                        v-model="formData['streetNumber']"
                                                        :required="!!formElement.config.required"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="formElement.config.mapping === 'zip'">
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="zip"
                                        v-model="formData['zip']"
                                        :name="'part[' + formElement.id + '][0]'"
                                        :required="!!formElement.config.required"
                                    />
                                    <ZipValidationMessage :zip="formData['zip']" :country="formData['country']" />
                                    <div class="alert alert-danger mb-1" v-if="(formErrors?.zip ?? []).length > 0">
                                        <div v-for="err in formErrors.zip" :key="err">{{ err }}</div>
                                    </div>
                                </div>
                                <div class="row" v-else-if="formElement.config.mapping === 'zip+city'">
                                    <input
                                        type="hidden"
                                        :name="'part[' + formElement.id + '][0]'"
                                        :value="`${formData['zip'] ?? ''}\t${formData['city'] ?? ''}`"
                                    />
                                    <div class="col-md-6">
                                        <div class="form-group flex-column">
                                            <div class="flex-grow-1 align-items-center d-sm-flex">
                                                <label for="zip" class="col-form-label required">
                                                    {{ $t("@cms:onlineForm.zip") }}
                                                </label>
                                                <div class="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="zip"
                                                        maxlength="10"
                                                        v-model="formData['zip']"
                                                        :required="!!formElement.config.required"
                                                    />
                                                    <ZipValidationMessage
                                                        :zip="formData['zip']"
                                                        :country="formData['country']"
                                                    />
                                                    <div
                                                        class="alert alert-danger mb-1"
                                                        v-if="(formErrors?.zip ?? []).length > 0"
                                                    >
                                                        <div v-for="err in formErrors.zip" :key="err">{{ err }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group flex-column" data-type="text">
                                            <div class="flex-grow-1 d-flex">
                                                <label for="city" class="col-form-label required">
                                                    {{ $t("@cms:onlineForm.city") }}
                                                </label>
                                                <div class="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="city"
                                                        v-model="formData['city']"
                                                        :required="!!formElement.config.required"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else-if="formElement.config.mapping === 'country'">
                                    <select
                                        v-model="formData['country']"
                                        class="form-control"
                                        :name="'part[' + formElement.id + '][0]'"
                                    >
                                        <option v-for="(name, iso) of countries" :value="iso" :key="iso">
                                            {{ name }}
                                        </option>
                                    </select>
                                </div>

                                <div v-else-if="formElement.config.mapping === 'birthDate'">
                                    <input
                                        type="date"
                                        class="form-control"
                                        :name="'part[' + formElement.id + '][0]'"
                                        v-model="formData['birthDate']"
                                        :required="!!formElement.config.required"
                                    />
                                </div>

                                <div v-else-if="formElement.config.mapping == 'organization' && appConfig.account">
                                    <select
                                        v-model="formData['organization']"
                                        class="form-control"
                                        :name="'part[' + formElement.id + '][0]'"
                                    >
                                        <option
                                            v-for="organization of props.organizations"
                                            :value="organization.name"
                                            :key="organization.id"
                                        >
                                            {{ organization.label }}
                                        </option>
                                    </select>
                                </div>

                                <div v-else v-for="(line, $index) in formElement.config.lines" :key="$index">
                                    <input
                                        :type="
                                            formElement.config.mapping?.includes('+') ||
                                            formElement.config.mapping === 'country'
                                                ? 'hidden'
                                                : 'text'
                                        "
                                        class="form-control my-1"
                                        :maxlength="line.numChars"
                                        :name="'part[' + formElement.id + '][' + $index + ']'"
                                        v-model="
                                            formData[formElement.config.mapping ?? formElement.text + '_' + $index]
                                        "
                                        :required="!!formElement.config.required"
                                    />
                                    <div
                                        class="alert alert-danger mb-1"
                                        v-if="(formErrors?.[formElement.config.mapping] ?? []).length > 0"
                                    >
                                        <div v-for="err in formErrors?.[formElement.config.mapping]" :key="err">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="my-3" v-if="formElement.type === 'radio'">
                                <label class="d-block font-weight-bold">{{ formElement.text }}</label>

                                <label v-for="line in formElement.choices" class="d-block" :key="line">
                                    <input
                                        type="radio"
                                        :value="line"
                                        v-model="formData[formElement.config.mapping ?? formElement.text]"
                                        :name="'part[' + formElement.id + ']'"
                                    />
                                    {{ line }}
                                </label>
                            </div>
                            <div class="my-3" v-if="formElement.type === 'checkbox'">
                                <label>
                                    <input
                                        type="checkbox"
                                        :name="'part[' + formElement.id + ']'"
                                        v-model="formData[formElement.text]"
                                        value="1"
                                    />
                                    {{ formElement.text }}
                                </label>
                            </div>
                            <div class="my-3" v-if="formElement.type === 'date'">
                                <label class="d-block font-weight-bold">{{ formElement.text }}</label>
                                <input
                                    type="date"
                                    :name="'part[' + formElement.id + ']'"
                                    v-model="formData[formElement.config.mapping ?? formElement.text]"
                                    class="form-control"
                                />
                            </div>
                            <div class="my-3" v-if="formElement.type === 'signature'">
                                <LegalFormSignature
                                    :form-element="formElement"
                                    @empty="isEmpty => onEmpty(isEmpty, formElement)"
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </form>
    </div>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped>
.onlineform-text-content span {
    background-color: transparent !important;
}

.signature-container {
    display: grid;
}

.signature-container .signature-pad {
    grid-column: 2 / span 1;
    grid-row: 1;
}

.signature-container .signature-label-city {
    border-top: 1px solid #333;
    grid-column: 1 / span 1;
    grid-row: 2;
}

.signature-container .signature-label-signature {
    border-top: 1px solid #333;
    grid-column: 2 / span 1;
    grid-row: 2;
}
</style>
